import { useState } from 'react';

interface UpscalePanelProps {
  imageUrl: string;
  darkMode: boolean;
  onUpscaleComplete: (multiplier: number) => void;
}

const UPSCALE_STEPS = [
  { label: 'Analyzing source resolution', icon: 'ri-search-eye-line' },
  { label: 'Denoising & sharpening edges', icon: 'ri-focus-3-line' },
  { label: 'AI upscaling pass', icon: 'ri-ai-generate' },
  { label: 'Enhancing fine details', icon: 'ri-magic-line' },
  { label: 'Finalizing & encoding', icon: 'ri-check-double-line' },
];

export default function UpscalePanel({ imageUrl, darkMode, onUpscaleComplete }: UpscalePanelProps) {
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [selectedMultiplier, setSelectedMultiplier] = useState<2 | 4 | null>(null);
  const [completedMultiplier, setCompletedMultiplier] = useState<2 | 4 | null>(null);

  const handleUpscale = async (multiplier: 2 | 4) => {
    if (isUpscaling) return;
    setIsUpscaling(true);
    setSelectedMultiplier(multiplier);
    setCompletedMultiplier(null);
    setCurrentStep(0);

    const delayPerStep = multiplier === 4 ? 900 : 650;
    for (let i = 0; i < UPSCALE_STEPS.length; i++) {
      setCurrentStep(i);
      await new Promise(r => setTimeout(r, delayPerStep));
    }

    setIsUpscaling(false);
    setCurrentStep(-1);
    setCompletedMultiplier(multiplier);
    onUpscaleComplete(multiplier);
  };

  const progress = isUpscaling && currentStep >= 0
    ? Math.round(((currentStep + 1) / UPSCALE_STEPS.length) * 100)
    : completedMultiplier ? 100 : 0;

  return (
    <div className={`rounded-2xl border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className={`px-5 py-4 border-b flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
            <i className="ri-zoom-in-line text-white text-sm"></i>
          </div>
          <div>
            <h4 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI Upscale</h4>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Enhance resolution without quality loss</p>
          </div>
        </div>
        {completedMultiplier && (
          <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold rounded-full">
            <i className="ri-check-line"></i>{completedMultiplier}× Applied
          </span>
        )}
      </div>

      <div className="p-5">
        {/* Multiplier buttons */}
        {!isUpscaling && (
          <div className="grid grid-cols-2 gap-3 mb-4">
            {([2, 4] as const).map(mult => (
              <button
                key={mult}
                onClick={() => handleUpscale(mult)}
                className={`relative flex flex-col items-center gap-1.5 py-4 rounded-xl border-2 font-semibold transition-all group ${
                  completedMultiplier === mult
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                    : darkMode
                    ? 'border-gray-600 hover:border-teal-500 text-gray-300 hover:text-teal-400 hover:bg-teal-900/10'
                    : 'border-gray-200 hover:border-teal-500 text-gray-700 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                <span className="text-2xl font-black tracking-tight">{mult}×</span>
                <span className={`text-xs font-medium ${darkMode ? 'text-gray-500 group-hover:text-teal-500' : 'text-gray-400 group-hover:text-teal-500'}`}>
                  {mult === 2 ? '2048px output' : '4096px output'}
                </span>
                {mult === 4 && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] font-bold rounded-full whitespace-nowrap">
                    MAX DETAIL
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Progress state */}
        {isUpscaling && (
          <div className="space-y-4">
            {/* Image preview with shimmer */}
            <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <img src={imageUrl} alt="Upscaling" className="w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-white/90 dark:bg-gray-800/90 flex items-center justify-center mx-auto mb-2 shadow-lg">
                    <i className="ri-loader-4-line animate-spin text-teal-500 text-xl"></i>
                  </div>
                  <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedMultiplier}× Upscaling...
                  </span>
                </div>
              </div>
              {/* Shimmer sweep */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-2">
              {UPSCALE_STEPS.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    i < currentStep ? 'bg-emerald-500' : i === currentStep ? 'bg-teal-500 animate-pulse' : darkMode ? 'bg-gray-700' : 'bg-gray-200'
                  }`}>
                    {i < currentStep ? (
                      <i className="ri-check-line text-white text-[10px]"></i>
                    ) : i === currentStep ? (
                      <i className="ri-loader-4-line text-white text-[10px] animate-spin"></i>
                    ) : (
                      <span className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></span>
                    )}
                  </div>
                  <span className={`text-xs transition-all ${
                    i <= currentStep
                      ? darkMode ? 'text-white font-medium' : 'text-gray-900 font-medium'
                      : darkMode ? 'text-gray-600' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className={`rounded-full h-1.5 overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className={`text-xs text-right font-medium ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>{progress}%</p>
          </div>
        )}

        {/* Completed state */}
        {!isUpscaling && completedMultiplier && (
          <div className="rounded-xl overflow-hidden">
            <div className="relative">
              <img src={imageUrl} alt="Upscaled" className="w-full object-cover rounded-xl" style={{ aspectRatio: '16/9' }} />
              <div className="absolute top-2 left-2 flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-bold rounded-lg shadow">
                <i className="ri-checkbox-circle-fill"></i>
                {completedMultiplier}× Upscaled
              </div>
            </div>
            <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Enhanced to {completedMultiplier === 2 ? '2048' : '4096'}px · Upscale again to further enhance
            </p>
          </div>
        )}

        {/* Tip */}
        {!isUpscaling && !completedMultiplier && (
          <p className={`text-xs text-center ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            <i className="ri-lightbulb-line mr-1"></i>
            4× is recommended for printing and large displays
          </p>
        )}
      </div>
    </div>
  );
}
